
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuthSession } from './auth/useAuthSession';
import { useAdminAccessCheck } from './auth/useAdminAccessCheck';

export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userRole, setUserRole] = useState<'admin' | 'gestionnaire' | 'assistant' | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const authCheckCompletedRef = useRef(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Hook extrait pour la gestion de session
  const { session, setSession } = useAuthSession();
  
  // Hook extrait pour la vérification des accès admin
  const { checkAdminAccess } = useAdminAccessCheck({
    setIsAuthenticated,
    setUserRole,
    setUserName,
    setIsLoading,
    authCheckCompletedRef,
    toast
  });

  useEffect(() => {
    // Éviter de refaire la vérification si elle a déjà été effectuée
    if (authCheckCompletedRef.current) return;
    
    let mounted = true;
    console.log("Setting up auth checking...");
    
    // Configuration de l'écouteur d'état d'authentification AVANT tout (critique pour éviter les conditions de course)
    //
    // ⚠️ CE CALLBACK NE DOIT RIEN ATTENDRE DE SUPABASE — ni requête, ni RPC, ni signOut.
    //
    // supabase-js v2 sérialise ses opérations d'authentification derrière un verrou
    // (navigator.locks) et appelle ce callback PENDANT qu'il le tient. Toute requête
    // lancée ici a besoin du jeton d'accès, donc réclame le même verrou : elle attend
    // un verrou que son propre appelant ne relâchera qu'une fois qu'elle aura répondu.
    // Interblocage, et la promesse ne se résout jamais.
    //
    // C'est ce qui figeait l'admin au retour sur l'onglet : le rafraîchissement du jeton
    // déclenchait ce callback, `await checkAdminAccess(...)` s'y bloquait, isLoading
    // restait à true, et RequireAdmin DÉMONTAIT ses enfants — détruisant l'écran ouvert
    // et, avec lui, le devis en cours de saisie.
    //
    // Le callback est donc synchrone, et tout appel Supabase est repoussé d'un tick,
    // hors du verrou. (Le chemin « cache valide » de useAdminAccessCheck le faisait
    // déjà ; celui sans cache, non — d'où un blocage qui n'apparaissait qu'après une
    // minute d'absence.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("Auth state changed:", event, Boolean(currentSession));

        if (!mounted) return;

        // Stocker la session
        setSession(currentSession);

        if (!currentSession) {
          // Pas de session signifie non authentifié
          setIsAuthenticated(false);
          setUserRole(null);
          setIsLoading(false);
          authCheckCompletedRef.current = true;
          return;
        }

        // Un simple rafraîchissement de jeton ne change PAS les droits : revérifier
        // relancerait une requête inutile toutes les heures et à chaque retour d'onglet.
        if (event === 'TOKEN_REFRESHED' && authCheckCompletedRef.current) return;

        setTimeout(() => {
          if (mounted) void checkAdminAccess(currentSession.user.id);
        }, 0);
      }
    );

    // PUIS vérification de l'existence d'une session (juste après avoir configuré l'écouteur)
    const initSession = async () => {
      if (authCheckCompletedRef.current) return;
      
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (currentSession) {
          console.log("Existing session found");
          setSession(currentSession);
          await checkAdminAccess(currentSession.user.id);
        } else {
          console.log("No existing session");
          if (mounted) {
            setIsAuthenticated(false);
            setIsLoading(false);
            authCheckCompletedRef.current = true;
          }
        }
      } catch (error) {
        console.error("Error checking session:", error);
        if (mounted) {
          setIsAuthenticated(false);
          setIsLoading(false);
          authCheckCompletedRef.current = true;
        }
      }
    };

    initSession();
    
    return () => {
      mounted = false; // Empêcher les mises à jour d'état après le démontage
      subscription.unsubscribe();
    };
  }, []);

  // Fonction de déconnexion optimisée
  const logout = async () => {
    // Nettoyer le cache et le stockage local avant déconnexion
    sessionStorage.removeItem('admin_role');
    sessionStorage.removeItem('admin_name');
    sessionStorage.removeItem('admin_cache_timestamp');
    sessionStorage.removeItem('supabase_session');
    
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setUserRole(null);
    setUserName(null);
    setSession(null);
    navigate('/admin/login', { replace: true });
  };

  return { 
    isAuthenticated, 
    isLoading, 
    userRole, 
    userName, 
    logout,
    session 
  };
};
